/* eslint-disable max-lines */
import React from 'react';

import Dropzone, {
  Accept,
  DropzoneInputProps,
  DropzoneRootProps,
  FileRejection,
} from 'react-dropzone';

import { CloseIcon } from '../Icons/CloseIcon';
import NextImage from 'next/image';

import { AnimationCube } from './components/AnimationCube';
import { UploadingOverlay } from './components/UploadingOverlay';

// Reference: https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Complete_list_of_MIME_types
const DEFAULT_ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

interface IProps {
  id: string;
  acceptedFileTypes?: string[];
  onUpload: (files: File[]) => Promise<boolean>;
  onClear?: () => void;
  dimensions?: {
    width: number;
    height: number;
  };
  label: string;
  imageUrl: string;
  multiple?: boolean;
  selectionLabel?: string;
  selectionIcon?: string | React.JSX.Element;
  browseLabel?: string;
  className?: string;
}

interface IState {
  errorMessage: string;
  uploading: boolean;
  files: File[];
}

class FileDropzone extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      errorMessage: '',
      uploading: false,
      files: [],
    };
  }

  isImageValid = (image: HTMLImageElement): Error | void => {
    const { dimensions } = this.props;
    if (
      dimensions &&
      (image.width !== Math.floor(dimensions.width) ||
        image.height !== Math.floor(dimensions.height))
    ) {
      return new Error(
        `Please upload an image with the following dimensions: ${Math.floor(
          dimensions.width,
        )}x${Math.floor(dimensions.height)}`,
      );
    }

    return undefined;
  };

  validateImages = async (files: File[]): Promise<Error | void> => {
    const validateFile = async (file: File): Promise<Error | void> =>
      new Promise(resolve => {
        const reader = new FileReader();
        reader.onabort = () => {
          const errorMessage = 'File upload was aborted.';
          this.setState({ errorMessage }, () => resolve(new Error(errorMessage)));
        };
        reader.onerror = () => {
          const errorMessage = 'Error while loading the files.';
          this.setState({ errorMessage }, () => resolve(new Error(errorMessage)));
        };
        reader.onload = () => {
          const dataURL = reader.result;
          const image = new Image();
          image.src = dataURL as string;

          image.onload = () => {
            const error = this.isImageValid(image);
            if (error) {
              this.setState({ errorMessage: error.message }, () => resolve(error));
            }
            resolve();
          };
        };
        reader.readAsDataURL(file);
      });

    return files.reduce(
      async (promise: Promise<Error | void>, file) =>
        promise.then(async error => (error ? Promise.resolve(error) : validateFile(file))),
      Promise.resolve(undefined),
    );
  };

  onAcceptationHandler = (files: File[]): void => {
    const { onUpload, dimensions } = this.props;
    const handleUploadState = (error?: Error | void): void => {
      if (!error) {
        this.setState({ errorMessage: '', uploading: true }, async () =>
          onUpload(files)
            .then(() => this.setState({ uploading: false, files }))
            .catch(() =>
              this.setState({
                errorMessage: 'Error happened while uploading the image.',
                uploading: false,
              }),
            ),
        );
      } else {
        this.setState({ errorMessage: error.message });
      }
    };

    if (dimensions) {
      this.validateImages(files)
        .then(handleUploadState)
        .catch(error => {
          throw error;
        });
    } else {
      handleUploadState();
    }
  };

  // Had to change the type from File[] in order to fit the react-dropzone package. Cannot test if the type change works since the "Drop file" functionality seems to be broken for this component
  onRejectionHandler = (files: FileRejection[]): void => {
    const { multiple } = this.props;
    const errorMessageBase = 'Selected files are not supported.';

    if (!multiple && files.length > 1) {
      this.setState({ errorMessage: `${errorMessageBase} Please select only one file.` });
    } else {
      this.setState({ errorMessage: errorMessageBase });
    }
  };

  onClear = (): void => {
    const { onClear } = this.props;
    if (onClear) {
      onClear();
    }
    this.setState({ files: [] });
  };

  // eslint-disable-next-line class-methods-use-this
  listOfSupportedTypes = (acceptedFileTypes?: string[]): string =>
    (acceptedFileTypes || DEFAULT_ACCEPTED_MIME_TYPES)
      .map(type => type.replace('.', ''))
      .map(type => {
        const [mimeType, extension] = type.split('/');
        return (extension || mimeType).toUpperCase();
      })
      .join(', ');

  public render() {
    const { id, label, dimensions, acceptedFileTypes, imageUrl, multiple } = this.props;
    const { uploading, errorMessage, files } = this.state;
    const uploadingOverlay = () => {
      const CUBE_DELAYS = ['0.2s', '0.3s', '0.4s', '0.1s', '0.2s', '0.3s', '0s', '0.1s', '0.2s'];

      return (
        <UploadingOverlay isUploading={uploading}>
          <div className="my-8 h-10 w-10">
            {CUBE_DELAYS.map((delay, i) => (
              <AnimationCube key={i} animationDelay={delay} />
            ))}
          </div>
          <div>Uploading...</div>
        </UploadingOverlay>
      );
    };

    const errorBox = (): React.JSX.Element | null =>
      errorMessage ? (
        <div className="flex rounded-xl bg-error px-5 py-1 text-white">{errorMessage}</div>
      ) : null;

    const selectionSection = (openFileSelection: () => void) => {
      const { selectionLabel, selectionIcon, browseLabel } = this.props;

      return (
        <div className="my-auto w-full text-center text-midnight">
          {selectionIcon ? <div>{selectionIcon}</div> : ''}
          <div className="text-base">
            {selectionLabel || 'Select the files you wish to upload.'}
          </div>
          <div className="text-base">
            &nbsp;
            <div className="inline-block cursor-pointer text-primary underline">
              <span
                role="button"
                tabIndex={0}
                onClick={openFileSelection}
                onKeyDown={openFileSelection}
              >
                {browseLabel || 'Browse your device.'}
              </span>
            </div>
          </div>
        </div>
      );
    };

    const infoContainer = () => (
      <div className="text-center text-midnight">
        {this.listOfSupportedTypes(acceptedFileTypes)}

        {dimensions ? (
          <div>
            {Math.floor(dimensions.width)}
            &nbsp;&times;&nbsp;
            {Math.floor(dimensions.height)}
          </div>
        ) : null}
      </div>
    );

    const DropzoneComponent = (
      { rootRef, rootProps }: DropzoneRootProps,
      inputProps: DropzoneInputProps,
      openFileSelection: () => void,
    ) => (
      <div
        // eslint-disable-next-line react/no-unknown-property
        refkey={rootRef}
        className="rounded-block-card relative mt-2 flex h-80 w-full flex-col items-center border border-dashed border-twilight p-8"
        {...rootProps}
      >
        <input {...inputProps} />
        {uploadingOverlay()}
        {errorBox()}
        {selectionSection(openFileSelection)}
        {infoContainer()}
      </div>
    );

    const dropzoneWrapper = () => (
      <Dropzone
        disabled={uploading}
        accept={(acceptedFileTypes || DEFAULT_ACCEPTED_MIME_TYPES) as unknown as Accept}
        multiple={multiple}
        onDropAccepted={this.onAcceptationHandler}
        onDropRejected={this.onRejectionHandler}
        noClick
        noKeyboard
      >
        {
          ({ getRootProps, getInputProps, open }) =>
            DropzoneComponent(getRootProps(), getInputProps({ id }), open)
          // eslint-disable-next-line
        }
      </Dropzone>
    );

    const preview = () => (
      <div className="rounded-block-card relative flex h-80 w-full flex-col items-center overflow-hidden border border-dashed border-twilight">
        {!imageUrl && files.length > 0 && (
          <>
            <h2>Uploaded Files</h2>
            <ul>
              {files.map((file, i) => (
                <li key={`${i}-${file.name}`}>{file.name}</li>
              ))}
            </ul>
          </>
        )}
        {imageUrl && (
          <NextImage
            src={imageUrl}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
            alt="Upload preview"
            unoptimized
            width={200}
            height={200}
          />
        )}
        <div className="absolute right-8 top-4 cursor-pointer rounded-sm bg-white px-1 py-px text-midnight transition-all">
          <span role="button" tabIndex={0} onKeyDown={this.onClear} onClick={this.onClear}>
            <div className="p-1 text-midnight">
              <CloseIcon />
            </div>
          </span>
        </div>
      </div>
    );
    return (
      <div className={this.props.className}>
        <label id={`${id}-label`} htmlFor={id} className="font-semibold">
          {label}
        </label>
        {imageUrl || files.length > 0 ? preview() : dropzoneWrapper()}
      </div>
    );
  }
}

export { FileDropzone };
